import { useState, useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { createPortal } from 'react-dom'
import { Settings as SettingsIcon } from 'lucide-react'
import { logout } from '../lib/auth-client'
import { useCurrentUser } from '../hooks/useCurrentUser'
import ContactSupportModal from './ContactSupportModal'

export default function UserMenu() {
  const navigate = useNavigate()
  const { data: user, isLoading: loading, refetch } = useCurrentUser()
  const [showMenu, setShowMenu] = useState(false)
  const [showContactModal, setShowContactModal] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null)

  useEffect(() => {
    const handleAuthChange = () => {
      void refetch()
    }
    window.addEventListener('storage', handleAuthChange)
    window.addEventListener('auth-change', handleAuthChange)
    return () => {
      window.removeEventListener('storage', handleAuthChange)
      window.removeEventListener('auth-change', handleAuthChange)
    }
  }, [refetch])

  const handleLogout = async () => {
    setShowMenu(false)
    await logout()
    navigate({ to: '/login' })
  }

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        right: window.innerWidth - rect.right + window.scrollX,
      })
    } else {
      setDropdownPosition(null)
    }
  }, [showMenu])

  const isDisabled = loading || !user

  return (
    <>
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => !isDisabled && setShowMenu(!showMenu)}
          disabled={isDisabled}
          className={`p-2.5 rounded-full transition-colors w-11 h-11 flex items-center justify-center ${
            isDisabled
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
          aria-label="User menu"
        >
          {loading ? (
            <div className="h-5 w-5 flex items-center justify-center">
              <div className="h-3 w-3 rounded-full bg-gray-300 animate-pulse" />
            </div>
          ) : (
            <SettingsIcon size={20} />
          )}
        </button>

        {showMenu && user && dropdownPosition && typeof window !== 'undefined' &&
          createPortal(
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div
                className="fixed w-56 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20 overflow-hidden"
                style={{ top: `${dropdownPosition.top}px`, right: `${dropdownPosition.right}px` }}
              >
                <div className="py-1">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{user.name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      navigate({ to: '/settings' })
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Account Settings
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      navigate({ to: '/settings/helper-app' })
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                  >
                    Set up Helper App
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      setShowContactModal(true)
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                  >
                    Contact Support
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </>,
            document.body,
          )}
      </div>
      <ContactSupportModal open={showContactModal} onOpenChange={setShowContactModal} />
    </>
  )
}
